/**
 * CancelCampaignDialog
 *
 * "Anuluj kampanię" — cancels an offer/campaign from any status, including
 * CONFIRMED. Reuses the offer's existing edit-access check (canEdit), same
 * as every other header action. Explicitly states that cancelling never
 * releases the offer's reservations (offer-lifecycle-reversibility).
 */
import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { ApiErrorCode, CancelOfferSchema, parseApiError } from '@ooh/contracts';
import type { Offer } from '@ooh/contracts';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import { DropdownMenuItem } from '@ooh/ui/components/dropdown-menu';
import { Label } from '@ooh/ui/components/label';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Textarea } from '@ooh/ui/components/textarea';
import { toast } from 'sonner';
import { useCancelOffer } from '../api';

// Derived from the server-side schema so a bound change never silently
// diverges between client validation and the API (CancelOfferSchema.reason).
const REASON_MIN_LENGTH = CancelOfferSchema.shape.reason.minLength ?? 5;
const REASON_MAX_LENGTH = CancelOfferSchema.shape.reason.maxLength ?? 500;

interface CancelCampaignReasonForm {
  reason: string;
}

export function CancelCampaignMenuItem({
  canEdit,
  isCancelled,
  onSelect,
}: {
  canEdit: boolean;
  isCancelled: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation('offers');

  if (!canEdit || isCancelled) {
    return null;
  }

  return (
    <DropdownMenuItem data-testid="cancel-campaign-menu-item" onSelect={onSelect}>
      {t('workspace.header.menu.cancelCampaign')}
    </DropdownMenuItem>
  );
}

export function CancelCampaignDialog({ offer, onClose }: { offer: Offer; onClose: () => void }) {
  const { t } = useTranslation('offers');
  const cancelOffer = useCancelOffer();
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<CancelCampaignReasonForm>({
    defaultValues: { reason: '' },
    mode: 'onChange',
  });
  const reasonValue = useWatch({ control, defaultValue: '', name: 'reason' });

  function closeDialog() {
    reset({ reason: '' });
    onClose();
  }

  function validateReason(value: string) {
    const trimmedValue = value.trim();

    if (trimmedValue.length < REASON_MIN_LENGTH) {
      return t('workspace.header.cancelDialog.errors.reasonMin');
    }

    if (trimmedValue.length > REASON_MAX_LENGTH) {
      return t('workspace.header.cancelDialog.errors.reasonMax');
    }

    return true;
  }

  async function handleCancelSubmit(values: CancelCampaignReasonForm) {
    try {
      await cancelOffer.mutateAsync({ id: String(offer.id), reason: values.reason.trim() });

      closeDialog();
      toast.success(t('workspace.header.toasts.cancelled'));
    } catch (error) {
      const parsedError = parseApiError(error);

      if (
        parsedError.statusCode === 409 &&
        parsedError.errorCode === ApiErrorCode.OFFER_ALREADY_CANCELLED
      ) {
        toast.error(t('workspace.header.toasts.cancelAlreadyCancelled'));
        return;
      }

      if (parsedError.statusCode === 409 && parsedError.errorCode === ApiErrorCode.OFFER_TRASHED) {
        toast.error(t('workspace.header.toasts.cancelOfferTrashed'));
        return;
      }

      if (parsedError.statusCode === 403) {
        toast.error(t('workspace.header.toasts.cancelForbidden'));
        return;
      }

      toast.error(t('workspace.header.toasts.cancelError'));
    }
  }

  return (
    <ModalDialog
      closeLabel={t('workspace.header.cancelDialog.close')}
      footer={
        <>
          <Button type="button" variant="outline" onClick={closeDialog}>
            {t('workspace.header.cancelDialog.cancel')}
          </Button>
          <Button
            type="submit"
            variant="destructive"
            // eslint-disable-next-line i18next/no-literal-string -- form id, not user-visible
            form="cancel-campaign-form"
            disabled={!isValid || cancelOffer.isPending}
          >
            {t('workspace.header.cancelDialog.confirm')}
          </Button>
        </>
      }
      header={
        <div>
          <h2 className="text-base font-semibold">{t('workspace.header.cancelDialog.title')}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t('workspace.header.cancelDialog.description')}
          </p>
        </div>
      }
      onClose={closeDialog}
      testId="cancel-campaign-dialog"
    >
      <form id="cancel-campaign-form" onSubmit={handleSubmit(handleCancelSubmit)}>
        <div className="space-y-1.5">
          <Label htmlFor="cancel-campaign-reason">
            {t('workspace.header.cancelDialog.reasonLabel')}
          </Label>
          <Textarea
            id="cancel-campaign-reason"
            aria-invalid={errors.reason ? true : undefined}
            error={Boolean(errors.reason)}
            placeholder={t('workspace.header.cancelDialog.reasonPlaceholder')}
            {...register('reason', { validate: validateReason })}
          />
          {errors.reason ? (
            <p className="text-xs text-destructive">{errors.reason.message}</p>
          ) : null}
          <div className="flex justify-end text-xs text-muted-foreground">
            <span>
              {reasonValue.trim().length}/{REASON_MAX_LENGTH}
            </span>
          </div>
        </div>
      </form>
    </ModalDialog>
  );
}
